import React, { useState } from "react";
import {
    Box,
    Typography,
    Paper,
    TextField,
    Button,
    Stack,
    IconButton,
    Input,
    CircularProgress,
    FormControlLabel,
    Switch,
} from "@mui/material";
import { Upload, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { createPressData, updatePressData } from "../../Services/PressManagement/PressApi";
import { useGlobalSnackbar } from "../../Components/Common/GlobalSanckbar";

const AddEditPressData = () => {
    const navigate = useNavigate();
    const { showSnackbar } = useGlobalSnackbar();
    const location = useLocation();
    const editData = location.state?.press;

    const [email, setEmail] = useState(editData?.email || "");
    const [yogaInfo, setYogaInfo] = useState(editData?.yogaInfo || "");
    const [yogaInfoLink, setYogaInfoLink] = useState(editData?.yogaInfoLink || "");
    const [pressReleaseLink, setPressReleaseLink] = useState(editData?.pressReleaseLink || "");
    const [brandAssetsLink, setBrandAssetsLink] = useState(editData?.brandAssetsLink || "");
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(editData?.headerImage || null);
    const [status, setStatus] = useState(editData?.status ?? true);
    const [loading, setLoading] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith("image/")) {
                setFieldErrors(prev => ({ ...prev, pressHeaderImage: "Only image files (PNG, JPG, WEBP, SVG, GIF) are allowed" }));
                showSnackbar("Only image files are allowed", "error");
                e.target.value = "";
                return;
            }

            setImage(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);

            if (fieldErrors.pressHeaderImage) {
                setFieldErrors(prev => ({ ...prev, pressHeaderImage: null }));
            }
        }
    };

    const handleRemoveImage = () => {
        setImage(null);
        setImagePreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (image && !image.type.startsWith("image/")) {
            setFieldErrors(prev => ({ ...prev, pressHeaderImage: "Only image files are allowed" }));
            showSnackbar("Only image files are allowed", "error");
            return;
        }

        setLoading(true);
        setFieldErrors({});

        const formData = new FormData();
        formData.append("email", email);
        formData.append("yogaInfo", yogaInfo);
        formData.append("yogaInfoLink", yogaInfoLink);
        formData.append("pressReleaseLink", pressReleaseLink);
        formData.append("brandAssetsLink", brandAssetsLink);
        formData.append("status", status);

        if (image) {
            formData.append("pressHeaderImage", image);
        }

        try {
            if (editData) {
                const pressId = editData.id || editData._id;
                await updatePressData(pressId, formData);
                showSnackbar("Press data updated successfully!", "success");
            } else {
                await createPressData(formData);
                showSnackbar("Press data created successfully!", "success");
            }

            setTimeout(() => navigate("/press-management"), 1500);
        } catch (err) {
            console.error(err);
            const responseData = err.response?.data;
            const errMsg = responseData?.message || "Failed to save press data.";
            showSnackbar(errMsg, "error");

            const errorsObj = {};
            if (responseData && responseData.details && Array.isArray(responseData.details)) {
                responseData.details.forEach(item => {
                    errorsObj[item.field] = item.message;
                });
                setFieldErrors(errorsObj);
            } else if (responseData && responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                responseData.errors.forEach(errStr => {
                    const str = errStr.toLowerCase();
                    if (str.includes("email")) {
                        errorsObj.email = errStr;
                    } else if (str.includes("yoga info link")) {
                        errorsObj.yogaInfoLink = errStr;
                    } else if (str.includes("yoga info")) {
                        errorsObj.yogaInfo = errStr;
                    } else if (str.includes("press release")) {
                        errorsObj.pressReleaseLink = errStr;
                    } else if (str.includes("brand asset")) {
                        errorsObj.brandAssetsLink = errStr;
                    }
                });
                setFieldErrors(errorsObj);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#FBFBFB", minHeight: "100vh" }}>


            <Typography sx={{ fontSize: "1.8rem", fontWeight: 500, mb: 4 }}>
                {editData ? "Edit Press Data" : "Add New Press Data"}
            </Typography>

            <Paper sx={{ p: 4, borderRadius: 3, border: "1px solid #E8E8E8", maxWidth: 800, mx: "auto" }}>
                {/* {success && <Alert severity="success" sx={{ mb: 3 }}>Saved successfully! Redirecting...</Alert>} */}

                <form onSubmit={handleSubmit}>
                    <Stack spacing={3}>

                        {/* Email Field */}
                        <Box>
                            <Typography sx={{ mb: 1, fontWeight: 500 }}>
                                Email
                            </Typography>
                            <TextField
                                type="email"
                                size="small"
                                fullWidth
                                placeholder="Enter email address"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    if (fieldErrors.email) {
                                        setFieldErrors(prev => ({ ...prev, email: '' }));
                                    }
                                }}
                                error={Boolean(fieldErrors.email)}
                                helperText={fieldErrors.email}
                            />
                        </Box>

                        {/* Yoga Info - React Quill */}
                        <Box sx={{
                            '& .quill': {
                                '& .ql-toolbar': {
                                    borderColor: fieldErrors.yogaInfo ? '#d32f2f' : '#ccc'
                                },
                                '& .ql-container': {
                                    borderColor: fieldErrors.yogaInfo ? '#d32f2f' : '#ccc'
                                }
                            }
                        }}>
                            <Typography sx={{ mb: 1, fontWeight: 500, color: fieldErrors.yogaInfo ? '#d32f2f' : 'inherit' }}>
                                Yoga Info
                            </Typography>
                            <ReactQuill
                                theme="snow"
                                value={yogaInfo}
                                onChange={(value) => {
                                    setYogaInfo(value);
                                    if (fieldErrors.yogaInfo) {
                                        setFieldErrors(prev => ({ ...prev, yogaInfo: '' }));
                                    }
                                }}
                                style={{ height: "200px", marginBottom: fieldErrors.yogaInfo ? "105px" : "85px" }}
                                modules={{
                                    toolbar: [
                                        [{ header: [1, 2, false] }],
                                        ["bold", "italic", "underline"],
                                        [{ list: "ordered" }, { list: "bullet" }],
                                        ["link", "clean"]
                                    ],
                                }}
                            />
                            {fieldErrors.yogaInfo && (
                                <Typography variant="caption" sx={{ color: '#d32f2f', mt: 0.5, display: 'block' }}>
                                    {fieldErrors.yogaInfo}
                                </Typography>
                            )}
                        </Box>

                        <TextField
                            label="Yoga Info Link"
                            fullWidth
                            size="small"
                            value={yogaInfoLink}
                            onChange={(e) => {
                                setYogaInfoLink(e.target.value);
                                if (fieldErrors.yogaInfoLink) {
                                    setFieldErrors(prev => ({ ...prev, yogaInfoLink: '' }));
                                }
                            }}
                            error={Boolean(fieldErrors.yogaInfoLink)}
                            helperText={fieldErrors.yogaInfoLink}
                        />

                        <TextField
                            label="Press Release Link"
                            fullWidth
                            size="small"
                            value={pressReleaseLink}
                            onChange={(e) => {
                                setPressReleaseLink(e.target.value);
                                if (fieldErrors.pressReleaseLink) {
                                    setFieldErrors(prev => ({ ...prev, pressReleaseLink: '' }));
                                }
                            }}
                            error={Boolean(fieldErrors.pressReleaseLink)}
                            helperText={fieldErrors.pressReleaseLink}
                        />

                        <TextField
                            label="Brand Assets Link"
                            fullWidth
                            size="small"
                            value={brandAssetsLink}
                            onChange={(e) => {
                                setBrandAssetsLink(e.target.value);
                                if (fieldErrors.brandAssetsLink) {
                                    setFieldErrors(prev => ({ ...prev, brandAssetsLink: '' }));
                                }
                            }}
                            error={Boolean(fieldErrors.brandAssetsLink)}
                            helperText={fieldErrors.brandAssetsLink}
                        />

                        {/* Image Upload */}
                        <Box sx={{ mb: 3 }}>
                            <Typography sx={{ mb: 1.5, fontWeight: 500 }}>
                                Press Image
                            </Typography>

                            {!imagePreview ? (
                                <Box component="label"
                                    sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        border: fieldErrors.pressHeaderImage ? "2px dashed #d32f2f" : "2px dashed #E0E0E0",
                                        borderRadius: 2,
                                        p: 4,
                                        textAlign: "center",
                                        cursor: "pointer",
                                        bgcolor: "#FAFAFA",
                                        transition: "all 0.2s",
                                        "&:hover": {
                                            borderColor: "#FFBD65",
                                            bgcolor: "#FFFDF9"
                                        }
                                    }}
                                >
                                    <Input
                                        type="file"
                                        inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml" }}
                                        sx={{ display: "none" }}
                                        onChange={handleImageChange}
                                    />
                                    <Upload size={32} color={fieldErrors.pressHeaderImage ? "#d32f2f" : "#888"} style={{ marginBottom: 8 }} />
                                    <Typography color={fieldErrors.pressHeaderImage ? "error" : "textSecondary"} sx={{ fontWeight: 500, fontSize: "0.9rem" }}>Click to upload image</Typography>
                                </Box>
                            ) : (
                                <Box sx={{ position: "relative", width: "fit-content", mx: "auto" }}>
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ width: "300px", height: "300px", objectFit: "cover", borderRadius: 12, display: "block" }}
                                    />
                                    <IconButton
                                        onClick={handleRemoveImage}
                                        size="small"
                                        sx={{
                                            position: "absolute",
                                            top: 8,
                                            right: 8,
                                            bgcolor: "#FFFFFF",
                                            color: "#EF4444",
                                            border: "1px solid #E2E8F0",
                                            boxShadow: "none",
                                            "&:hover": {
                                                bgcolor: "#FEE2E2",
                                                color: "#DC2626"
                                            }
                                        }}
                                    >
                                        <X size={16} color="#EF4444" />
                                    </IconButton>
                                </Box>
                            )}
                            {fieldErrors.pressHeaderImage && (
                                <Typography color="error" variant="caption" sx={{ mt: 1, display: "block", textAlign: "center" }}>
                                    {fieldErrors.pressHeaderImage}
                                </Typography>
                            )}
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={status}
                                    onChange={(e) => setStatus(e.target.checked)}
                                />
                            }
                            label="Active Status"
                        />

                        <Stack direction="row" spacing={2}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate("/press-management")}
                            >
                                Cancel
                            </Button>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{ bgcolor: "#FFBD65", color: "#000" }}
                            >
                                {loading
                                    ? <CircularProgress size={20} />
                                    : editData
                                        ? "Update Press Data"
                                        : "Create Press Data"}
                            </Button>
                        </Stack>

                    </Stack>
                </form>
            </Paper>
        </Box>
    );
};

export default AddEditPressData;
